import { useContext } from 'react';

import { Button, TTranslator } from '@smartshell/ui-kit';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import { LocalizationContext, TLocalesKeys } from 'localization';
import { transformTime } from 'utils';

import { TGetPausableTariffTableColumns } from './interfaces';
import s from './PausableTariff.module.scss';

dayjs.extend(utc);
dayjs.extend(timezone);

const formatSchedules = (
  schedules: { end: number; start: number }[]
): Array<{
  day: string;
  start: string;
  end: string;
}> => {
  const { t } = useContext(LocalizationContext);

  const daysOfWeek = [
    t('weekdays.monday'),
    t('weekdays.tuesday'),
    t('weekdays.wednesday'),
    t('weekdays.thursday'),
    t('weekdays.friday'),
    t('weekdays.saturday'),
    t('weekdays.sunday'),
  ];

  return schedules.map((schedule) => {
    const dayIndex = Math.floor((schedule.start - 1) / (24 * 60 * 60)) % 7;
    const dayOfWeek = daysOfWeek[dayIndex];

    const startDate = dayjs.unix(schedule.start).utc();
    const endDate = dayjs.unix(schedule.end).utc();

    return {
      day: dayOfWeek,
      start: startDate.format('HH:mm'),
      end: endDate.format('HH:mm'),
    };
  });
};

export const getColumns: TGetPausableTariffTableColumns = (
  t,
  stopClientSession
) => [
  {
    Header: t('title'),
    accessor: 'tariff',
    Cell: ({ value }) => value.title,
    width: 171,
    disableResizing: true,
    disableSortBy: true,
  },
  {
    Header: t('report.hosts.group'),
    accessor: 'host_group',
    Cell: ({ value: { title } }) => title,
    width: 124,
    disableResizing: true,
    disableSortBy: true,
  },
  {
    Header: t('client.pausable.rest'),
    accessor: ({ elapsed, duration }) => duration - elapsed,
    Cell: ({ value }: { value: number }) => {
      const restTime = transformTime(value, t);

      return restTime?.title || null;
    },
    width: 125,
    disableResizing: true,
  },
  {
    Header: t('subscription.until'),
    accessor: 'expires_at',
    Cell: ({ value }) => dayjs(value).format('DD.MM.YYYY HH:mm'),
    width: 156,
    disableResizing: true,
  },
  {
    Header: t('tariff.schedule'),
    accessor: ({ tariff: { schedules } }) => schedules,
    Cell: ({ value }: { value: { end: number; start: number }[] }) => {
      const schedules = formatSchedules(value);

      return (
        <div className={s.schedules}>
          <div className={s.day_block}>
            {schedules.map((item, index) => (
              <span key={index} className={s.day}>
                {item.day},{' '}
              </span>
            ))}
          </div>
          <div className={s.time_block}>
            {schedules.map((item, index) => (
              <div key={index}>
                {index === schedules.length - 1 && (
                  <span key={index} className={s.time}>
                    {item.start} — {item.end}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      );
    },
    width: 176,
    disableResizing: true,
  },
  {
    accessor: 'id',
    Cell: ({ value }) => (
      <Button
        variant="outline"
        size="s"
        onClick={() =>
          stopClientSession({
            variables: {
              id: value,
              ignorePause: true,
            },
          })
        }
      >
        {t('pausable_tariff.stop')}
      </Button>
    ),
    width: 230,
    disableSortBy: true,
    disableResizing: true,
  },
];

export const getDurationString = (
  t: TTranslator<TLocalesKeys>,
  seconds: number
): string => {
  const sec = seconds % 60;
  const allMin = Math.floor(seconds / 60);
  const h = Math.floor(allMin / 60);
  const min = allMin % 60;

  const array = [];

  if (h > 0) {
    array.push(`${h} ${t('hour')}`);
  }

  if (min > 0) {
    array.push(`${min} ${t('minute')}`);
  }

  if (sec > 0) {
    array.push(`${sec} ${t('second')}`);
  }

  return array.join(' ');
};
